import {
  DEAL_STATUSES,
  ShortReservation,
  ShortGuest,
  Offer,
  OFFER_CONFIRMATION_TYPES,
} from '@chekin/core';
import {
  useBookExperienceStore,
  useItemQuantitiesByOfferId,
  selectEmail,
} from 'store/bookExperience';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useGuestGroup, useFetchOffer, useCreateBulkDealMutation, usePath} from 'hooks';
import {useBuildNavigationItems} from 'hooks/useBuildNavigationItems';
import {useReservation} from 'context/reservation';
import {PATHS} from 'routes/constants';
import {BACK_TO_OFFER_LIST} from 'components/upselling/usePathToOfferView';
import {usePostHogTrack, POSTHOG_EVENTS} from 'analytics';

type BuildPayloadProps = {
  reservation: ShortReservation;
  offer: Offer;
  email?: string;
  coreGuest?: ShortGuest;
  itemQuantities: Record<string, number>;
};

const buildPayload = ({
  reservation,
  email,
  offer,
  coreGuest,
  itemQuantities,
}: BuildPayloadProps) => {
  const requestedItems = Object.entries(itemQuantities)
    .filter(([, qty]) => qty > 0)
    .map(([itemId, qty]) => ({
      item: itemId,
      quantity: qty,
    }));

  const totalQuantity = Object.values(itemQuantities).reduce((sum, q) => sum + q, 0);

  return {
    deals: [
      {
        core_housing_id: reservation?.housing_id,
        core_reservation_id: reservation?.id,
        core_guest_name: coreGuest?.full_name,
        core_housing_name: reservation.housing_name,
        core_room_numbers: reservation.rooms.map(room => room.number),
        guest_email: email,
        core_guest_id: coreGuest?.id,
        offer_id: offer.id,
        offerName: offer.title,
        quantity: 1,
        payments_version: 'V2' as const,
        number_of_people: totalQuantity,
        requested_items: requestedItems,
        status:
          offer.confirmation_type === OFFER_CONFIRMATION_TYPES.auto
            ? DEAL_STATUSES.approved
            : DEAL_STATUSES.pre_requested,
      },
    ],
  };
};

type Props = {
  offerId?: string;
  onSuccess?: () => void;
  onError?: (errors: Error[]) => void;
};
export const useBookDeal = ({offerId, onSuccess, onError}: Props) => {
  const [searchParams] = useSearchParams();
  const bookExperienceEmail = useBookExperienceStore(selectEmail);
  const {data: reservation, isLoading: isLoadingReservation} = useReservation();
  const {data: offer, isLoading: isLoadingOffer} = useFetchOffer({id: offerId || ''});
  const {guestGroup} = useGuestGroup();
  const itemQuantities = useItemQuantitiesByOfferId(offerId);
  const setBookExperienceEmail = useBookExperienceStore(state => state.setEmail);
  const resetBookExperienceItemQuantities = useBookExperienceStore(
    state => state.resetItemQuantities,
  );
  const {getFullPath} = usePath();
  const navigate = useNavigate();
  const {getPaymentPath} = useBuildNavigationItems();
  const {trackEvent, trackOfferEvent} = usePostHogTrack();

  const isBackToOfferList = searchParams.has(BACK_TO_OFFER_LIST);

  const {mutate, isPending: isCreating} = useCreateBulkDealMutation({
    onSuccess() {
      const totalQuantity = Object.values(itemQuantities).reduce((sum, q) => sum + q, 0);
      if (offer) {
        trackOfferEvent(POSTHOG_EVENTS.OFFER_MANUALLY_ADDED, offer, {
          quantity: totalQuantity,
        });
      } else {
        trackEvent(POSTHOG_EVENTS.OFFER_MANUALLY_ADDED, {quantity: totalQuantity});
      }
      onSuccess?.();
      if (offerId) {
        resetBookExperienceItemQuantities(offerId);
      }
    },
    onError,
  });

  const createDeal = (email?: string) => {
    if (email) {
      setBookExperienceEmail(email);
    }
    const coreGuest = guestGroup?.members.find(({id}) => id === guestGroup?.leader_id);

    const hasItemQuantities = Object.values(itemQuantities).some(q => q > 0);

    if (!isCreating && reservation && offer && hasItemQuantities) {
      mutate(
        buildPayload({
          reservation,
          offer,
          coreGuest,
          email,
          itemQuantities,
        }),
      );
    }
  };

  const getEmail = () => {
    return (
      guestGroup?.members.find(({id}) => id === guestGroup?.leader_id)?.email ||
      reservation?.default_invite_email
    );
  };

  const navigateToOffer = () => {
    if (isBackToOfferList) {
      navigate(getFullPath(`recommendations/${offerId}?${BACK_TO_OFFER_LIST}=true`));
    } else {
      navigate(getFullPath(`recommendations/${offerId}`));
    }
  };

  const navigateToConfirmEmail = () => {
    if (isBackToOfferList) {
      navigate(
        getFullPath(
          `recommendations/${offerId}/confirm-email?${BACK_TO_OFFER_LIST}=true`,
        ),
      );
    } else {
      navigate(getFullPath(`recommendations/${offerId}/confirm-email`));
    }
  };

  const navigateToBookExperience = (options?: {replace: boolean}) => {
    if (isBackToOfferList) {
      navigate(
        getFullPath(
          `recommendations/${offerId}/${PATHS.recommendations.details.bookExperience}?${BACK_TO_OFFER_LIST}=true`,
        ),
        {
          replace: options?.replace,
        },
      );
    } else {
      navigate(
        getFullPath(
          `recommendations/${offerId}/${PATHS.recommendations.details.bookExperience}`,
        ),
        {
          replace: options?.replace,
        },
      );
    }
  };

  const navigateAfterSuccess = () => {
    if (isBackToOfferList) {
      navigate(getFullPath(PATHS.recommendations.main));
    } else {
      navigate(getFullPath(PATHS.home));
    }
  };

  const navigateToCart = () => {
    navigate(getFullPath(getPaymentPath()));
  };

  const navigateToRecommendations = () => {
    navigate(getFullPath(PATHS.recommendations.main));
  };

  return {
    createDeal,
    getEmail,
    navigateToConfirmEmail,
    navigateAfterSuccess,
    navigateToCart,
    navigateToRecommendations,
    navigateToOffer,
    navigateToBookExperience,
    bookExperienceEmail,
    offer,
    itemQuantities,
    isLoading: isLoadingReservation || isLoadingOffer,
  };
};
